// ----------------------------------------------------------------------------
// mixer — Mixer panel for DAWSEUR
//
// Signal flow: track vol/pan → FX → track EQ → master EQ → master pan → master vol
//
// Each channel strip: Name | EQ(lo/mid/hi) | Pan | Vol(vert) | M/S
// Master strip:       MASTER | EQ(lo/mid/hi) | Pan | Vol(vert)
// ----------------------------------------------------------------------------

const { makeKnob: sharedMakeKnob, showKnobContextMenu } = require('./knob.js');

function createMixer(opts = {}) {
   const container = document.getElementById('lw-mixer-body');
   if (!container) return null;

   const state = {
      engine: opts.engine || null,
      tracks: opts.tracks || [],
      vuMeters: new Map(), // trackId → { bar, peakBar, peakHold, peakTime }
      masterVu: null,
      vuRaf: null,
      onVolumeChange: opts.onVolumeChange || (() => {}),
      onPanChange: opts.onPanChange || (() => {}),
      onMuteChange: opts.onMuteChange || (() => {}),
      onSoloChange: opts.onSoloChange || (() => {}),
      onEqChange: opts.onEqChange || (() => {}),
      onMasterVolume: opts.onMasterVolume || (() => {}),
      onMasterPan: opts.onMasterPan || (() => {}),
      onMasterEq: opts.onMasterEq || (() => {}),
      onMasterComp: opts.onMasterComp || (() => {}),
      onMasterCompParam: opts.onMasterCompParam || (() => {}),
   };

   // ---- VU meter helpers ----
   function makeVuMeter() {
      const wrap = document.createElement('div');
      wrap.className = 'lw-mx-vu';

      const bar = document.createElement('div');
      bar.className = 'lw-mx-vu-bar';
      wrap.appendChild(bar);

      const peak = document.createElement('div');
      peak.className = 'lw-mx-vu-peak';
      wrap.appendChild(peak);

      return { wrap, bar, peak };
   }

   function updateVu(meter, level) {
      if (!meter) return;
      // level is RMS 0-1, scale to percentage with some curve
      const pct = Math.min(100, Math.pow(level, 0.6) * 120);
      meter.bar.style.height = pct + '%';

      // Peak hold
      if (pct > meter.peakHold) {
         meter.peakHold = pct;
         meter.peakTime = performance.now();
      } else if (performance.now() - meter.peakTime > 800) {
         meter.peakHold = Math.max(0, meter.peakHold - 3);
      }
      meter.peak.style.bottom = meter.peakHold + '%';
   }

   function startVuLoop() {
      if (state.vuRaf) return;
      function tick() {
         const eng = state.engine;
         if (eng) {
            for (const [trackId, meter] of state.vuMeters) {
               const level = eng.getTrackLevel ? eng.getTrackLevel(trackId) : 0;
               updateVu(meter, level);
            }
            if (state.masterVu) {
               const level = eng.getMasterLevel ? eng.getMasterLevel() : 0;
               updateVu(state.masterVu, level);
            }
         }
         state.vuRaf = requestAnimationFrame(tick);
      }
      state.vuRaf = requestAnimationFrame(tick);
   }

   function stopVuLoop() {
      if (state.vuRaf) {
         cancelAnimationFrame(state.vuRaf);
         state.vuRaf = null;
      }
   }

   // ---- Helper: create a rotary knob (delegates to shared implementation) ----
   function makeKnob(label, min, max, step, value, onChange, knobId) {
      const formatFn = (v) => {
         if (label === 'PAN') return v === 0 ? 'C' : (v < 0 ? 'L' + Math.round(-v) : 'R' + Math.round(v));
         if (label === 'VOL') return Math.round(v * 100) + '%';
         return (v > 0 ? '+' : '') + v.toFixed(1);
      };
      return sharedMakeKnob(label, min, max, step, value, onChange, formatFn, { id: knobId });
   }

   // ---- Helper: create a vertical volume slider (custom, no native range input) ----
   function makeVFader(label, min, max, step, value, onChange, faderId) {
      const wrap = document.createElement('div');
      wrap.className = 'lw-mx-vfader';

      const lab = document.createElement('div');
      lab.className = 'lw-mx-vfader-label';
      lab.textContent = label;
      wrap.appendChild(lab);

      // Custom track + thumb (no native <input type="range"> to avoid browser hover-reset bugs)
      const track = document.createElement('div');
      track.className = 'lw-mx-vtrack';
      const fill = document.createElement('div');
      fill.className = 'lw-mx-vfill';
      track.appendChild(fill);
      wrap.appendChild(track);

      const val = document.createElement('div');
      val.className = 'lw-mx-vfader-val';
      val.textContent = Math.round(value * 100) + '%';
      wrap.appendChild(val);

      let current = value;
      const defaultValue = value;
      const range = max - min;

      function formatVal(v) {
         if (label === 'VOL') return Math.round(v * 100) + '%';
         return (v > 0 ? '+' : '') + v.toFixed(2);
      }

      function setVisual() {
         const pct = ((current - min) / range) * 100;
         fill.style.height = pct + '%';
         val.textContent = formatVal(current);
      }

      function setValue(v) {
         current = Math.max(min, Math.min(max, v));
         // snap to step
         current = Math.round(current / step) * step;
         setVisual();
         onChange(current);
      }

      // Silent visual update (no onChange) — used by automation playback
      function setValueVisual(v) {
         current = Math.max(min, Math.min(max, v));
         current = Math.round(current / step) * step;
         setVisual();
      }

      setVisual();

      // Drag interaction — drag up/down on the track to change value
      let dragging = false;
      let startY = 0;
      let startVal = 0;

      const mouseMoveHandler = (e) => {
         if (!dragging) return;
         const dy = startY - e.clientY;
         const sensitivity = rect_height(track);
         const delta = (dy / sensitivity) * range;
         setValue(startVal + delta);
      };

      const mouseUpHandler = () => {
         if (dragging) {
            dragging = false;
            document.body.style.cursor = '';
         }
      };

      function rect_height(el) {
         return el.getBoundingClientRect().height || 100;
      }

      track.addEventListener('mousedown', (e) => {
         dragging = true;
         startY = e.clientY;
         startVal = current;
         e.preventDefault();
         document.body.style.cursor = 'ns-resize';
         // Also jump to clicked position
         const rect = track.getBoundingClientRect();
         const pct = 1 - (e.clientY - rect.top) / rect.height;
         setValue(min + pct * range);
      });

      document.addEventListener('mousemove', mouseMoveHandler);
      document.addEventListener('mouseup', mouseUpHandler);

      // Wheel
      track.addEventListener('wheel', (e) => {
         e.preventDefault();
         const dir = e.deltaY < 0 ? 1 : -1;
         setValue(current + dir * step * (e.shiftKey ? 10 : 1));
      }, { passive: false });

      // Double-click resets to default
      track.addEventListener('dblclick', () => {
         setValue(defaultValue);
      });

      // ---- Context menu (right-click) — same as knobs ----
      wrap.oncontextmenu = (e) => {
         const menuItems = [];
         if (typeof window !== 'undefined' && window.createAutomation) {
            menuItems.push({
               label: 'Automate',
               callback: () => {
                  window.createAutomation({
                     label: label,
                     min: min,
                     max: max,
                     step: step,
                     getValue: () => current,
                     setValue: setValueVisual,
                     element: wrap,
                     knobId: knobId,
                     standalone: true,
                  });
               },
            });
            menuItems.push('hr');
         }
         menuItems.push({
            label: 'Reset',
            callback: () => { setValue(defaultValue); },
         });
         menuItems.push({
            label: 'Link to controller',
            callback: () => {
               if (typeof window !== 'undefined') {
                  window._knobMidiLearn = { setValue, getValue: () => current, label, element: wrap };
                  if (window._knobMidiLearnCallback) window._knobMidiLearnCallback(window._knobMidiLearn);
               }
            },
         });
         menuItems.push({
            label: 'Unlink',
            callback: () => {
               if (typeof window !== 'undefined' && window._knobMidiLinks) {
                  delete window._knobMidiLinks[wrap.dataset.knobId];
               }
            },
         });
         showKnobContextMenu(e, menuItems);
      };

      // Expose setValue for external updates
      wrap.setValue = setValue;
      wrap.setValueVisual = setValueVisual;
      wrap.getValue = () => current;

      // Register in global registry for automation playback
      const knobId = faderId || ('fader_' + Math.random().toString(36).slice(2, 11));
      wrap.dataset.knobId = knobId;
      if (typeof window !== 'undefined') {
         if (!window._knobRegistry) window._knobRegistry = new Map();
         window._knobRegistry.set(knobId, { setValueVisual, getValue: () => current, label });
      }

      // Cleanup
      wrap.destroy = () => {
         document.removeEventListener('mousemove', mouseMoveHandler);
         document.removeEventListener('mouseup', mouseUpHandler);
         if (typeof window !== 'undefined' && window._knobRegistry) {
            window._knobRegistry.delete(knobId);
         }
      };

      return wrap;
   }

   // ---- Build a track channel strip ----
   function buildChannel(track, idx) {
      const ch = document.createElement('div');
      ch.className = 'lw-mx-channel';
      ch.dataset.trackId = track.id;

      // Track name
      const name = document.createElement('div');
      name.className = 'lw-mx-name';
      name.textContent = track.name || `Trk ${idx + 1}`;
      ch.appendChild(name);

      // EQ section (3 rotary knobs)
      const eq = document.createElement('div');
      eq.className = 'lw-mx-eq';
      eq.appendChild(makeKnob('LO', -24, 24, 0.5, track.eqLowGain || 0, (v) => state.onEqChange(track.id, 'low', v), 'mixer_' + track.id + '_eq_low'));
      eq.appendChild(makeKnob('MID', -24, 24, 0.5, track.eqMidGain || 0, (v) => state.onEqChange(track.id, 'mid', v), 'mixer_' + track.id + '_eq_mid'));
      eq.appendChild(makeKnob('HI', -24, 24, 0.5, track.eqHiGain || 0, (v) => state.onEqChange(track.id, 'hi', v), 'mixer_' + track.id + '_eq_hi'));
      ch.appendChild(eq);

      // Pan (rotary knob)
      const pan = makeKnob('PAN', -50, 50, 1, track.mixerPanVal || 0, (v) => state.onPanChange(track.id, v), 'mixer_' + track.id + '_pan');
      pan.classList.add('lw-mx-pan');
      ch.appendChild(pan);

      // Volume + VU meter side by side
      const volVu = document.createElement('div');
      volVu.className = 'lw-mx-volvu';
      const vol = makeVFader('VOL', 0, 1, 0.01, track.mixerVolume !== undefined ? track.mixerVolume : 0.8, (v) => state.onVolumeChange(track.id, v), 'mixer_' + track.id + '_vol');
      volVu.appendChild(vol);
      const vu = makeVuMeter();
      volVu.appendChild(vu.wrap);
      ch.appendChild(volVu);
      state.vuMeters.set(track.id, { bar: vu.bar, peak: vu.peak, peakHold: 0, peakTime: 0 });

      // Mute / Solo
      const btns = document.createElement('div');
      btns.className = 'lw-mx-btns';
      const muteBtn = document.createElement('button');
      muteBtn.className = 'lw-mx-btn lw-mx-mute';
      muteBtn.textContent = 'M';
      if (track.muted) muteBtn.classList.add('active');
      muteBtn.addEventListener('click', () => {
         track.muted = !track.muted;
         muteBtn.classList.toggle('active', track.muted);
         state.onMuteChange(track.id, track.muted);
      });
      const soloBtn = document.createElement('button');
      soloBtn.className = 'lw-mx-btn lw-mx-solo';
      soloBtn.textContent = 'S';
      if (track.solo) soloBtn.classList.add('active');
      soloBtn.addEventListener('click', () => {
         track.solo = !track.solo;
         soloBtn.classList.toggle('active', track.solo);
         state.onSoloChange(track.id, track.solo);
      });
      btns.appendChild(muteBtn);
      btns.appendChild(soloBtn);
      ch.appendChild(btns);

      return ch;
   }

   // ---- Build the master compressor/limiter block ----
   function buildMasterComp() {
      const ch = document.createElement('div');
      ch.className = 'lw-mx-channel lw-mx-comp';

      const name = document.createElement('div');
      name.className = 'lw-mx-name';
      name.textContent = 'COMP/LIM';
      ch.appendChild(name);

      // On/off switch
      const swWrap = document.createElement('div');
      swWrap.className = 'lw-mx-comp-switch';
      const swId = 'mx_comp_sw_' + Math.random().toString(36).substr(2, 9);
      const sw = document.createElement('input');
      sw.type = 'checkbox';
      sw.className = 'switch';
      sw.id = swId;
      sw.checked = false;
      const swLabel = document.createElement('label');
      swLabel.className = 'switchlabel';
      swLabel.htmlFor = swId;
      swWrap.appendChild(sw);
      swWrap.appendChild(swLabel);
      ch.appendChild(swWrap);

      sw.addEventListener('change', () => {
         state.onMasterComp(sw.checked);
      });

      // Compressor knobs
      const knobs = document.createElement('div');
      knobs.className = 'lw-mx-eq';
      knobs.appendChild(makeKnob('THR', -60, 0, 1, -6, (v) => state.onMasterCompParam('threshold', v), 'mixer_master_comp_threshold'));
      knobs.appendChild(makeKnob('RATIO', 1, 20, 0.5, 20, (v) => state.onMasterCompParam('ratio', v), 'mixer_master_comp_ratio'));
      knobs.appendChild(makeKnob('KNEE', 0, 40, 1, 0, (v) => state.onMasterCompParam('knee', v), 'mixer_master_comp_knee'));
      knobs.appendChild(makeKnob('ATK', 0, 1, 0.001, 0.003, (v) => state.onMasterCompParam('attack', v), 'mixer_master_comp_attack'));
      knobs.appendChild(makeKnob('REL', 0.01, 1, 0.01, 0.1, (v) => state.onMasterCompParam('release', v), 'mixer_master_comp_release'));
      ch.appendChild(knobs);

      // Spacer to match channel height
      const spacer = document.createElement('div');
      spacer.style.flex = '1';
      ch.appendChild(spacer);

      return ch;
   }

   // ---- Build the master strip ----
   function buildMaster() {
      const ch = document.createElement('div');
      ch.className = 'lw-mx-channel lw-mx-master';

      const name = document.createElement('div');
      name.className = 'lw-mx-name';
      name.textContent = 'MASTER';
      ch.appendChild(name);

      const eq = document.createElement('div');
      eq.className = 'lw-mx-eq';
      eq.appendChild(makeKnob('LO', -24, 24, 0.5, 0, (v) => state.onMasterEq('low', v), 'mixer_master_eq_low'));
      eq.appendChild(makeKnob('MID', -24, 24, 0.5, 0, (v) => state.onMasterEq('mid', v), 'mixer_master_eq_mid'));
      eq.appendChild(makeKnob('HI', -24, 24, 0.5, 0, (v) => state.onMasterEq('hi', v), 'mixer_master_eq_hi'));
      ch.appendChild(eq);

      const pan = makeKnob('PAN', -50, 50, 1, 0, (v) => state.onMasterPan(v));
      pan.classList.add('lw-mx-pan');
      ch.appendChild(pan);

      const volVu = document.createElement('div');
      volVu.className = 'lw-mx-volvu';
      const vol = makeVFader('VOL', 0, 1, 0.01, 1.0, (v) => state.onMasterVolume(v), 'mixer_master_vol');
      volVu.appendChild(vol);
      const vu = makeVuMeter();
      volVu.appendChild(vu.wrap);
      ch.appendChild(volVu);
      state.masterVu = { bar: vu.bar, peak: vu.peak, peakHold: 0, peakTime: 0 };

      const btns = document.createElement('div');
      btns.className = 'lw-mx-btns';
      btns.style.visibility = 'hidden';
      ch.appendChild(btns);

      return ch;
   }

   function render() {
      if (!container) return;
      // Destroy old knobs and faders before clearing (cleans up registry entries)
      const oldControls = container.querySelectorAll('.lw-mx-knob, .lw-mx-vfader');
      for (const k of oldControls) {
         if (k.destroy) k.destroy();
      }
      container.innerHTML = '';
      state.vuMeters.clear();
      state.masterVu = null;
      for (let i = 0; i < state.tracks.length; i++) {
         container.appendChild(buildChannel(state.tracks[i], i));
      }
      container.appendChild(buildMaster());
      container.appendChild(buildMasterComp());
      startVuLoop();
   }

   function setTracks(tracks) {
      state.tracks = tracks;
      render();
   }

   function setEngine(eng) {
      state.engine = eng;
   }

   function destroy() {
      stopVuLoop();
   }

   render();

   return { state, render, setTracks, setEngine, destroy };
}

module.exports = { createMixer };
